import React, { useRef, useState } from 'react';
import { useGoals } from '../../contexts/GoalsContext';
import {
  PERIODS, UNITS, PERIOD_LABELS, UNIT_LABELS, GOAL_PRESETS, makeGoal, unitTotal
} from '../../utils/goals';
import { downloadBackup } from '../../utils/backup';
import StreakCalendar from './StreakCalendar';
import ReminderSettings from './ReminderSettings';

const fieldClass =
  'w-full p-2 rounded-md border border-input-border bg-input-bg text-charcoal font-lato ' +
  'focus:border-deep-green focus:ring-2 focus:ring-deep-green';

const sectionTitle = 'text-lg font-semibold mb-3 text-heading border-b pb-2';

const GoalModal = ({ onClose }) => {
  const { goal, setGoal, log, progress, streak, projection, resetLog, restoreBackup, daysInARow, readToday, khatms, cycle, today } = useGoals();

  const [editing, setEditing] = useState(!goal);
  const [period, setPeriod] = useState(goal?.period || 'day');
  const [unit, setUnit] = useState(goal?.unit || 'verses');
  const [amount, setAmount] = useState(goal?.amount || 20);
  const [reminderHours, setReminderHours] = useState(goal?.reminderHours || [20]);
  const [reminderEnabled, setReminderEnabled] = useState(goal?.reminderEnabled !== false);
  const [notice, setNotice] = useState(null);
  const fileInput = useRef(null);

  const applyPreset = (preset) => {
    setPeriod(preset.period);
    setUnit(preset.unit);
    setAmount(preset.amount);
  };

  const save = (event) => {
    event.preventDefault();
    setGoal(makeGoal({ period, unit, amount, reminderHours, reminderEnabled }));
    setEditing(false);
  };

  const importBackup = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const result = restoreBackup(JSON.parse(reader.result));
        setNotice(result.ok ? 'Backup restored.' : result.reason);
      } catch (parseError) {
        setNotice('That file could not be read.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const maxForUnit = unitTotal(unit);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Reading goal"
    >
      <div
        className="rounded-lg shadow-xl max-w-lg w-full p-6 my-8 animate-fadeIn"
        style={{ backgroundColor: 'var(--color-card-bg)' }}
      >
        <div className="flex justify-between items-center mb-6 pb-3 border-b" style={{ borderColor: 'var(--color-card-border)' }}>
          <h2 className="text-2xl font-bold text-heading font-kufi">Reading goal</h2>
          <button
            onClick={onClose}
            className="opacity-60 hover:opacity-100 focus:outline-none"
            style={{ color: 'var(--color-charcoal)' }}
            aria-label="Close"
          >
            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {editing ? (
          <form onSubmit={save}>
            <div className="mb-6">
              <h3 className={sectionTitle} style={{ borderColor: 'var(--color-card-border)' }}>Start from a pace</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {GOAL_PRESETS.map((preset) => (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="text-left text-sm p-2 rounded-md border font-lato hover:bg-light-gray focus-visible:bg-light-gray transition-colors"
                    style={{ borderColor: 'var(--color-card-border)', color: 'var(--color-charcoal)' }}
                  >
                    {preset.label}
                    <span className="block text-xs opacity-70">
                      {preset.amount} {UNIT_LABELS[preset.unit]} / {PERIOD_LABELS[preset.period]}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h3 className={sectionTitle} style={{ borderColor: 'var(--color-card-border)' }}>Or set your own</h3>

              <div className="grid grid-cols-3 gap-3">
                <label className="block text-sm font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  How many
                  <input
                    type="number" min="1" max={maxForUnit} value={amount}
                    onChange={(e) => setAmount(Math.max(1, Math.min(maxForUnit, parseInt(e.target.value, 10) || 1)))}
                    className={`${fieldClass} mt-1`}
                  />
                </label>

                <label className="block text-sm font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  Of what
                  <select value={unit} onChange={(e) => setUnit(e.target.value)} className={`${fieldClass} mt-1`}>
                    {UNITS.map((option) => (
                      <option key={option} value={option}>{UNIT_LABELS[option]}</option>
                    ))}
                  </select>
                </label>

                <label className="block text-sm font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  Per
                  <select value={period} onChange={(e) => setPeriod(e.target.value)} className={`${fieldClass} mt-1`}>
                    {PERIODS.map((option) => (
                      <option key={option} value={option}>{PERIOD_LABELS[option]}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="mb-6">
              <h3 className={sectionTitle} style={{ borderColor: 'var(--color-card-border)' }}>Reminders</h3>
              <ReminderSettings
                hours={reminderHours}
                enabled={reminderEnabled}
                goalMetOn={progress?.met ? today : null}
                onChange={({ hours: nextHours, enabled }) => {
                  setReminderHours(nextHours);
                  setReminderEnabled(enabled);
                }}
              />
            </div>

            <div className="flex justify-end gap-3">
              {goal && (
                <button
                  type="button" onClick={() => setEditing(false)}
                  className="px-4 py-2 rounded-md bg-light-gray text-charcoal hover:bg-light-gold font-lato transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="px-6 py-2 rounded-md bg-button-bg text-button-text hover:bg-medium-green font-lato transition-colors"
              >
                Save goal
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-sm font-lato mb-1" style={{ color: 'var(--color-charcoal)', opacity: 0.7 }}>
                This {PERIOD_LABELS[goal.period]}
              </p>
              <p className="text-3xl font-bold text-heading font-kufi mb-3">
                {progress.done} / {progress.target}
                <span className="text-base font-normal ml-2 opacity-70">{UNIT_LABELS[goal.unit]}</span>
              </p>
              <div className="w-full rounded-full h-3" style={{ backgroundColor: 'var(--color-light-gray)' }}>
                <div
                  className="h-3 rounded-full transition-all duration-300"
                  style={{
                    width: `${progress.percent}%`,
                    backgroundColor: progress.met ? 'var(--color-gold)' : 'var(--color-medium-green)'
                  }}
                />
              </div>
              <p className="text-sm mt-2 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                {progress.met
                  ? 'Goal met for this period.'
                  : `${progress.remaining} ${UNIT_LABELS[goal.unit]} left.`}
                {streak > 0 && ` ${streak} ${PERIOD_LABELS[goal.period]}${streak > 1 ? 's' : ''} in a row.`}
              </p>
            </div>

            <div
              className="mb-6 p-4 rounded-lg flex items-center justify-between gap-4"
              style={{ backgroundColor: 'var(--color-light-gray)' }}
            >
              <div>
                <p className="text-3xl font-bold text-heading font-kufi">{daysInARow}</p>
                <p className="text-sm font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  day{daysInARow === 1 ? '' : 's'} in a row
                </p>
              </div>
              <p className="text-sm font-lato text-right" style={{ color: 'var(--color-charcoal)', opacity: 0.75 }}>
                {readToday
                  ? 'Read today ✓'
                  : 'One verse today keeps it going.'}
              </p>
            </div>

            <div className="mb-6">
              <h3 className={sectionTitle} style={{ borderColor: 'var(--color-card-border)' }}>Overall</h3>

              {khatms > 0 && (
                <p className="text-xl font-bold text-heading font-kufi mb-2">
                  Quran completed {khatms} time{khatms > 1 ? 's' : ''}
                </p>
              )}

              <p className="font-lato text-sm" style={{ color: 'var(--color-charcoal)' }}>
                {khatms > 0 ? 'This reading: ' : ''}
                {projection.completed} of {projection.total} {UNIT_LABELS[goal.unit]} read
                {' '}({projection.percent.toFixed(1)}%).
              </p>
              {goal.unit !== 'verses' && (
                <p className="font-lato text-sm mt-1" style={{ color: 'var(--color-charcoal)', opacity: 0.75 }}>
                  {cycle.read} of {cycle.total} verses ({cycle.percent.toFixed(1)}%).
                </p>
              )}
              {projection.finishDate && (
                <p className="font-lato text-sm mt-1" style={{ color: 'var(--color-charcoal)', opacity: 0.75 }}>
                  At this pace the Quran is finished around{' '}
                  {projection.finishDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long' })}.
                </p>
              )}
            </div>

            <div className="mb-6">
              <h3 className={sectionTitle} style={{ borderColor: 'var(--color-card-border)' }}>History</h3>
              <StreakCalendar />
            </div>

            <div className="mb-6">
              <h3 className={sectionTitle} style={{ borderColor: 'var(--color-card-border)' }}>Reminders</h3>
              <ReminderSettings
                hours={goal.reminderHours || []}
                enabled={goal.reminderEnabled !== false}
                goalMetOn={progress.met ? today : null}
                onChange={({ hours: nextHours, enabled }) =>
                  setGoal({ ...goal, reminderHours: nextHours, reminderEnabled: enabled })}
              />
            </div>

            <div className="mb-6">
              <h3 className={sectionTitle} style={{ borderColor: 'var(--color-card-border)' }}>Backup</h3>
              <p className="font-lato text-sm mb-3" style={{ color: 'var(--color-charcoal)', opacity: 0.75 }}>
                Your history lives in this browser only. Clearing site data would erase it.
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => downloadBackup(goal, log)}
                  className="px-4 py-2 rounded-md bg-light-gray text-charcoal hover:bg-light-gold font-lato transition-colors"
                >
                  Export
                </button>
                <button
                  onClick={() => fileInput.current?.click()}
                  className="px-4 py-2 rounded-md bg-light-gray text-charcoal hover:bg-light-gold font-lato transition-colors"
                >
                  Import
                </button>
                <input ref={fileInput} type="file" accept="application/json" onChange={importBackup} className="hidden" />
              </div>
              {notice && (
                <p className="text-sm mt-2 font-lato" role="status" style={{ color: 'var(--color-charcoal)' }}>{notice}</p>
              )}
            </div>

            <div className="flex flex-wrap justify-end gap-3 pt-4 border-t" style={{ borderColor: 'var(--color-card-border)' }}>
              <button
                onClick={() => {
                  if (window.confirm('Clear your reading history? The goal itself is kept.')) resetLog();
                }}
                className="px-4 py-2 rounded-md font-lato transition-colors"
                style={{ backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}
              >
                Clear history
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Remove this goal? Your reading history is kept.')) { setGoal(null); onClose(); }
                }}
                className="px-4 py-2 rounded-md bg-light-gray text-charcoal hover:bg-light-gold font-lato transition-colors"
              >
                Remove goal
              </button>
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 rounded-md bg-light-gray text-charcoal hover:bg-light-gold font-lato transition-colors"
              >
                Change goal
              </button>
              <button
                onClick={onClose}
                className="px-6 py-2 rounded-md bg-button-bg text-button-text hover:bg-medium-green font-lato transition-colors"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GoalModal;
